import { z } from "zod";

/** Single data point on the 12-month revenue vs. expenses sparkline. */
export const revenueTrendPointSchema = z.object({
	/** ISO year-month, e.g. "2025-04". */
	month: z.string().regex(/^\d{4}-\d{2}$/),
	/** Revenue for the month, integer cents. */
	revenue: z.number().int().nonnegative(),
	/** Source charges (COGS / pass-through vendor cost) for the month, integer cents. */
	cost: z.number().int().nonnegative(),
});
export type RevenueTrendPoint = z.infer<typeof revenueTrendPointSchema>;

// `revenueTrendInputSchema` retired in Phase 8 — fields moved into
// `dashboardFiltersSchema.trendMonths` (see ./dashboard-filters.ts).

/** The 4 stages of the monthly billing cycle. */
export const CYCLE_STAGE_KEYS = ["upload", "extract", "review", "push"] as const;
export type CycleStageKey = (typeof CYCLE_STAGE_KEYS)[number];

/** Stage state based on where the cycle is today. */
export const CYCLE_STAGE_STATES = ["complete", "active", "waiting"] as const;
export type CycleStageState = (typeof CYCLE_STAGE_STATES)[number];

/** Event types rendered as colored bars on the calendar. */
export const CYCLE_EVENT_TYPES = [
	"milestone",
	"upload",
	"extract",
	"review",
	"push",
	"deadline",
] as const;
export type CycleEventType = (typeof CYCLE_EVENT_TYPES)[number];

export const cycleEventSchema = z.object({
	day: z.number().int().min(1).max(31),
	type: z.enum(CYCLE_EVENT_TYPES),
	label: z.string(),
});
export type CycleEvent = z.infer<typeof cycleEventSchema>;

export const cycleStageItemSchema = z.object({
	label: z.string(),
	meta: z.string(),
	status: z.enum(["ok", "warn", "pending"]),
	/** When present, the stage item is rendered as a link to a filtered view
	 * that surfaces the underlying rows (e.g. "Needs attention" → /attention).
	 * Omitted for purely-informational counts like "Auto-matched". */
	href: z.string().optional(),
});
export type CycleStageItem = z.infer<typeof cycleStageItemSchema>;

export const cycleStageStatusSchema = z.object({
	key: z.enum(CYCLE_STAGE_KEYS),
	name: z.string(),
	dateRange: z.string(),
	state: z.enum(CYCLE_STAGE_STATES),
	primary: z.string(),
	secondary: z.string(),
	items: z.array(cycleStageItemSchema),
	cta: z.object({ label: z.string(), href: z.string() }).nullable(),
});
export type CycleStageStatus = z.infer<typeof cycleStageStatusSchema>;

// ----------------------------------------------------------------------------
// Cycle history — the "How this cycle compares" table on the /cycle page.
// One row per trailing calendar month, current cycle included.
// ----------------------------------------------------------------------------

export const cycleHistoryInputSchema = z.object({
	/** Number of trailing months to return, oldest→newest. Current cycle is
	 * always the last row; 4 means current + 3 closed months. */
	months: z.number().int().min(1).max(12).default(4),
});
export type CycleHistoryInput = z.infer<typeof cycleHistoryInputSchema>;

export const cycleHistoryRowSchema = z.object({
	/** Human-readable label, e.g. "April 2026". */
	monthLabel: z.string(),
	year: z.number().int(),
	/** 0-indexed month (0 = January), to match JS Date semantics. */
	month: z.number().int().min(0).max(11),
	invoiceCount: z.number().int().nonnegative(),
	/** Total billed revenue for invoices whose period falls in this month,
	 * integer cents. */
	revenueCents: z.number().int().nonnegative(),
	/** Count of extractions flagged (attentionReason != null) whose linked
	 * invoice's period falls in this month. */
	issuesResolved: z.number().int().nonnegative(),
	/** Days between the last pushedAt in the month and the month's last day.
	 * Null when the cycle is still open (no push yet, or month hasn't closed). */
	daysToClose: z.number().int().nullable(),
	/** True for the month containing "today" — drives the "in progress" pill. */
	isCurrent: z.boolean(),
});
export type CycleHistoryRow = z.infer<typeof cycleHistoryRowSchema>;

// ----------------------------------------------------------------------------
// Overview summary — hero numbers + YTD + delta copy on the / Overview page.
// Period-scoped MTD and YTD revenue live here so the Overview page can make
// one round-trip; the legacy `dashboardSummary` procedure still backs the
// sidebar badge and /cycle so this one stays narrowly focused.
// ----------------------------------------------------------------------------

export const overviewSummarySchema = z.object({
	/** "YYYY-MM-DD" — today, as the server sees it. Drives the greeting window. */
	today: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
	monthLabel: z.string(),
	yearLabel: z.string(),
	/** Count of non-deleted, active customers. Powers the "Active customers" hero. */
	activeCustomers: z.number().int().nonnegative(),
	/** Non-deleted draft count — mirrors dashboardSummary.draftsToReview. */
	draftsToReview: z.number().int().nonnegative(),
	mtd: z.object({
		revenueCents: z.number().int().nonnegative(),
		invoiceCount: z.number().int().nonnegative(),
		grossProfitCents: z.number().int(),
		marginPercent: z.number().int(),
		/** % of active customers whose invoice for this month has been created. */
		pctBilled: z.number().int().min(0).max(100),
	}),
	prevMonth: z.object({
		revenueCents: z.number().int().nonnegative(),
		marginPercent: z.number().int(),
	}),
	ytd: z.object({
		revenueCents: z.number().int().nonnegative(),
		invoiceCount: z.number().int().nonnegative(),
		grossProfitCents: z.number().int(),
		marginPercent: z.number().int(),
		newCustomers: z.number().int().nonnegative(),
		/** Avg days between period close and push across invoices pushed YTD.
		 * Null when nothing has been pushed this year. */
		avgDaysToClose: z.number().nullable(),
	}),
});
export type OverviewSummary = z.infer<typeof overviewSummarySchema>;

// ----------------------------------------------------------------------------
// Customer concentration — "Where the money came from" list on /.
// Top-N real customers oldest→newest by current-month revenue, plus a rolled-up
// "Everyone else (M)" row when there are more customers than the top-N cut-off.
// ----------------------------------------------------------------------------

// `customerConcentrationInputSchema` retired in Phase 8 — `topN` moved into
// `dashboardFiltersSchema.concentrationTopN` (see ./dashboard-filters.ts).

export const concentrationRowSchema = z.object({
	/** Null for the synthetic "Everyone else" row. */
	customerId: z.string().nullable(),
	customerName: z.string(),
	revenueCents: z.number().int().nonnegative(),
	/** 0–100, rounded to 1 decimal. */
	pctOfTotal: z.number().min(0).max(100),
	/** Count of customers rolled into this row. Always 1 for a real customer;
	 * the "Everyone else" row carries the count of remaining customers. */
	customerCount: z.number().int().nonnegative(),
	isOther: z.boolean(),
});
export type ConcentrationRow = z.infer<typeof concentrationRowSchema>;

export const customerConcentrationSchema = z.object({
	monthLabel: z.string(),
	totalRevenueCents: z.number().int().nonnegative(),
	rows: z.array(concentrationRowSchema),
});
export type CustomerConcentration = z.infer<typeof customerConcentrationSchema>;

export const currentCycleSchema = z.object({
	/** ISO date of the first day of the current cycle (YYYY-MM-DD). */
	openDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
	/** ISO date of the last day of the current cycle (YYYY-MM-DD). */
	closeDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
	/** ISO date of today (YYYY-MM-DD) — server-derived so the UI is consistent. */
	today: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
	/** Human-readable label, e.g. "April 2026". */
	monthLabel: z.string(),
	/** 1-indexed day of the cycle (1 on the 1st, increments daily). */
	dayOfCycle: z.number().int().min(1).max(31),
	/** Total days in the cycle (28–31). */
	totalDays: z.number().int().min(28).max(31),
	events: z.array(cycleEventSchema),
	stages: z.array(cycleStageStatusSchema),
});
export type CurrentCycle = z.infer<typeof currentCycleSchema>;
